import PT from 'prop-types';
import {
  TextField,
  Box,
  MenuItem,
  CircularProgress,
  ListItem,
  Typography,
  Chip,
} from '@mui/material';
import { Done, Clear } from '@mui/icons-material';
import { isEmpty, uniq } from 'lodash';
import { useTheme, lighten, darken } from '@mui/material/styles';
import { useContext } from 'react';
import { StyledIconButton } from 'components/buttons';
import { ThemeContext } from 'context/theme-context';
import useAddInstance from './hooks/use-add-instance';

const AddInstance = ({ type, subTypes, index, columnIndex }) => {
  const {
    isPosting,
    newAssets,
    setNewAssets,
    error,
    setError,
    onClick,
    onClearClick,
    selectedSubType,
    setSelectedSubType,
    inputValue,
    setInputValue,
  } = useAddInstance(index, columnIndex, type, subTypes);
  const { localTheme } = useContext(ThemeContext);
  const theme = useTheme();

  const handleChange = e => {
    if (e.target.value && e.target.value.trim() !== '') {
      setNewAssets(uniq([...newAssets, e.target.value]));
    }
  };

  const handleKey = e => {
    if (
      e.key === 'Tab' &&
      e.target.value &&
      e.target.value.trim() !== '' &&
      !newAssets.includes(e.target.value)
    ) {
      e.preventDefault();
      e.stopPropagation();
      handleChange(e);
    }
    if (
      e.key === 'Enter' &&
      e.target.value &&
      e.target.value.trim() !== '' &&
      !newAssets.includes(e.target.value)
    ) {
      e.preventDefault();
      e.stopPropagation();
      handleChange(e);
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    let paste = (e.clipboardData || window.clipboardData).getData('text');
    const pasted = paste.split(/[\t,\r\n]+/).map(value => value.trim());

    const uniqueValues = pasted.filter(
      value => value && !newAssets.includes(value)
    );
    if (uniqueValues.length > 0) {
      setNewAssets(curr => uniq([...curr, ...uniqueValues]));
    }
  };

  const handleNameDelete = item => {
    setNewAssets(newAssets.filter(v => v !== item));
  };

  const handleDelete = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNameDelete(e.target.innerText);
    }
  };

  return (
    <ListItem
      aria-label="add-instance"
      data-cy="add-instance"
      sx={{
        paddingTop: 0,
        paddingRight: theme => theme.spacing(9),
        paddingLeft: theme => theme.spacing(2),
      }}
      secondaryAction={
        isPosting ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <StyledIconButton
              title="Save"
              sx={theme => ({
                padding: 0,
                color: theme.palette.secondary.dark,
              })}
              onClick={onClick}
              disabled={error || (isEmpty(newAssets) && !inputValue)}
              data-cy="wizard-save-new"
              icon={<Done />}
            />
            <StyledIconButton
              title="Cancel"
              sx={theme => ({
                padding: 0,
                color: theme.palette.secondary.dark,
              })}
              onClick={onClearClick}
              data-cy="wizard-clear-new"
              icon={<Clear />}
            />
          </>
        )
      }
    >
      {!isEmpty(subTypes) ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            data-cy={`wizard-column-select-subType-${columnIndex}`}
            id="add-sub-type"
            aria-label="add-sub-type"
            sx={{ width: 150 }}
            error={!!error}
            color="primary"
            variant="standard"
            label={
              <Typography noWrap sx={{ width: 130 }}>
                Select Sub Type
              </Typography>
            }
            value={selectedSubType}
            onChange={e => {
              setSelectedSubType(e.target.value);
              setInputValue(e.target.value.name);
              setError('');
            }}
          >
            {subTypes.map(subType => (
              <MenuItem
                data-cy={`wizard-column-selected-subType-${columnIndex}`}
                key={subType.id}
                value={subType}
              >
                {subType.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            aria-label="add-sub-type-name"
            id="asset-sub-type"
            sx={{ width: 150 }}
            error={error}
            autoFocus
            color="primary"
            variant="standard"
            label={`New asset name`}
            value={inputValue}
            onChange={e => {
              if (e.target.value && !selectedSubType) {
                setError('Please select a sub type');
              }
              setInputValue(e.target.value);
            }}
            helperText={
              error
                ? error
                : 'hit "Tab" for multiple assets or paste list from excel'
            }
            onPaste={e => handlePaste(e)}
            onKeyDown={e => handleKey(e)}
            onBlur={e => handleChange(e)}
            slotProps={{
              htmlInput: {
                'data-cy': `wizard-column-add-text-${columnIndex}`,
                'aria-label': 'asset-sub-type-input',
              },
              input: {
                startAdornment:
                  newAssets.length === 0 ? null : (
                    <div>
                      {newAssets.map(name => {
                        return (
                          <Chip
                            key={name}
                            aria-label="newAssetNameChip"
                            label={name}
                            onKeyDown={e => handleDelete(e)}
                            variant="outlined"
                            sx={{
                              margin: theme => theme.spacing(0.25),
                              backgroundColor:
                                localTheme === 'dark'
                                  ? darken(theme.palette.primary.main, 0.3)
                                  : lighten(theme.palette.primary.main, 0.8),
                            }}
                            onDelete={() => {
                              handleNameDelete(name);
                            }}
                          />
                        );
                      })}
                    </div>
                  ),
              },
            }}
          />
        </Box>
      ) : (
        <TextField
          aria-label="add-free-text"
          id="asset-name"
          error={error}
          autoFocus
          color="primary"
          variant="standard"
          label={`${'Add'} ${type.name}`}
          value={inputValue}
          helperText={
            error
              ? error
              : 'hit "Tab" for multiple assets or paste list from excel'
          }
          onPaste={e => handlePaste(e)}
          onKeyDown={e => handleKey(e)}
          onBlur={e => handleChange(e)}
          onChange={e => setInputValue(e.target.value)}
          slotProps={{
            htmlInput: { 'data-cy': `wizard-column-add-text-${columnIndex}` },
            input: {
              startAdornment:
                newAssets.length === 0 ? null : (
                  <div>
                    {newAssets.map(name => {
                      return (
                        <Chip
                          key={name}
                          aria-label="newAssetNameChip"
                          label={name}
                          onKeyDown={e => handleDelete(e)}
                          variant="outlined"
                          sx={{
                            margin: theme => theme.spacing(0.25),
                            backgroundColor:
                              localTheme === 'dark'
                                ? darken(theme.palette.primary.main, 0.3)
                                : lighten(theme.palette.primary.main, 0.8),
                          }}
                          onDelete={() => {
                            handleNameDelete(name);
                          }}
                        />
                      );
                    })}
                  </div>
                ),
            },
          }}
        />
      )}
    </ListItem>
  );
};

AddInstance.propTypes = {
  columnIndex: PT.number.isRequired,
  type: PT.shape({
    id: PT.string,
    name: PT.string,
    isAssetSubType: PT.bool,
    children: PT.arrayOf(PT.shape({ parent: PT.string })),
    parent: PT.string,
    isSubType: PT.bool,
  }).isRequired,
  subTypes: PT.arrayOf(
    PT.shape({
      id: PT.string,
      name: PT.string,
    })
  ),
  index: PT.number.isRequired,
};

AddInstance.defaultProps = {
  subTypes: undefined,
};

export default AddInstance;
